import { FC, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router";

import { ERoutes } from "../../constants/routes";
import { addPrompt } from "../../store/prompts/slice";
import {
  selectActivePromptsByBrandState,
  selectLoading as selectPromptsLoading,
  selectPromptsLoadError,
} from "../../store/selectors/prompts";
import { fetchPrompts } from "../../store/prompts/slice";
import PromptsLayout from "../../layout/PromptsLayout/PromptsLayout";
import HeaderBrandsSelector from "../../components/HeaderBrandsSelector/HeaderBrandsSelector";
import {
  selectActiveBrandsState,
  selectBrandState,
  selectSelectedBrandState,
} from "../../store/selectors/brands";
import { TitleWithForm } from "../../components";
import { fetchBrandByPromptsSeries, resetDailyAveragePrompt } from "../../store/scores/slice";
import { setViewBrandId } from "../../store/brands/slice";
import {
  selectBrandEveryDay7dSummaryForViewBrand,
  selectLastUpdateTimeState,
} from "../../store/selectors/scores";
import CardWrapper from "../../components/CardWrapper/CardWrapper";
import CardPrompt from "../../components/CardPrompt/CardPrompt";
import { usePrevious } from "../../hooks";
import { AddFormItem } from "../../components/AddForm/types";

import styles from "./Prompts.module.css";



const PromptsView: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const brand = useSelector(selectBrandState);
  const brands = useSelector(selectActiveBrandsState);
  const prompts = useSelector(selectActivePromptsByBrandState(id));
  const promptsLoading = useSelector(selectPromptsLoading);
  const promptsError = useSelector(selectPromptsLoadError);
  const widgetSummary = useSelector(selectBrandEveryDay7dSummaryForViewBrand);
  const selectedBrand = useSelector(selectSelectedBrandState);
  const lastUpdateTime = useSelector(selectLastUpdateTimeState(Number(id)));
  const previousSelectedBrand = usePrevious(selectedBrand); 

  useEffect(() => {
    if (!selectedBrand) return;
    dispatch(fetchBrandByPromptsSeries({ brand_id: selectedBrand.id, days: 16 }));
  }, [selectedBrand, dispatch]);


  useEffect(() => {
    if (previousSelectedBrand?.id !== selectedBrand?.id) {
      dispatch(resetDailyAveragePrompt());
    }
  
    if (id && brands) {
      const changedBrand = brands.find(brand => brand.id == Number(id));
      if (!changedBrand && brand) {
        navigate(ERoutes.Prompts.replace(":id", String(brand.id)), { replace: true });
      }
      dispatch(setViewBrandId((changedBrand || brand)?.id ?? null));
    }

    if (!id) {
      if (brand) {
        dispatch(setViewBrandId(brand?.id ?? null));
        navigate(ERoutes.Prompts.replace(":id", String(brand.id)), { replace: true });
      }

      if (selectedBrand) {
        navigate(ERoutes.Prompts.replace(":id", String(selectedBrand.id)), { replace: true });
      }
    }
  }, [id, brands]);

  const handleAddPrompt = (item: AddFormItem) => {
    dispatch(addPrompt({ content: item.label }));
  };

  const handleChangeBrands = (brandId: number) => {
    dispatch(resetDailyAveragePrompt());
    navigate(ERoutes.Prompts.replace(":id", String(brandId)), { replace: true });
  };



  return (
    <PromptsLayout>
      <HeaderBrandsSelector
        className={styles['prompts__header']}
        onBrandChange={handleChangeBrands}
        score={widgetSummary}
        lastUpdateTime={lastUpdateTime}
      />

      <TitleWithForm
        className={styles["prompts__title"]}
        label="Prompts"
        onAddItem={handleAddPrompt}
        placeholder="Add a new prompt"
      />

      <CardWrapper
        items={prompts}
        skeletonCount={3}
        currentBrandId={id}
        component={CardPrompt}
        loading={promptsLoading}
        error={promptsError}
        onRetry={() => dispatch(fetchPrompts())}
      />

    </PromptsLayout>
  );
};

export default PromptsView;